import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '@environments/environment';
import { SavedCardAuthHeaders } from './saved-card.service';

export type SipPayApiEnv = 'DEV' | 'QA' | 'PRD';

const SIP_PAY_API_URLS: Record<SipPayApiEnv, string> = {
  DEV: 'https://dev.api.epago.pe',
  QA: 'https://qas.api.epago.pe',
  PRD: 'https://api.epago.pe',
};

declare const sha512: (message: string) => string;

export interface SipAuthorizeFormInput {
  orderNumber: string;
  amount: number;
  currencyCode: string;
  countryCode: string;
  language: string;
  documentType: string;
  documentNumber: string;
  sipPhone: string;
  customerIdentifier: string;
  name: string;
  lastName: string;
  email: string;
  customerPhone: string;
  addressCountry: string;
  addressLevel1: string;
  addressLevel2: string;
  addressLevel3: string;
  addressLine1: string;
  addressZip: string;
  productName: string;
  productSku: string;
  productCategory: string;
}

export interface AuthorizeSipRequest {
  order: {
    number: string;
    amount: number;
    discounts: number;
    taxes: number;
    fee: number;
    shippingAmount: number;
    country: { code: string };
    currency: { code: string };
    products: Array<{
      name: string;
      quantity: number;
      unitAmount: number;
      amount: number;
      sku: string;
      category: string;
      discount: number;
      shipping: number;
    }>;
    customer: {
      identifier: string;
      name: string;
      lastName: string;
      email: string;
      phone: string;
      address: {
        country: string;
        levels: string[];
        line1: string;
        zip: string;
      };
      document: { type: string; number: string };
    };
    mode: string;
  };
  settings: {
    expiration: null;
    language: string;
    paymentMethods: string[];
  };
  payment: {
    paymentMethod: string;
    cardholder: {
      document: { type: string; number: string };
      phone: string;
    };
    clientRequest: {
      browserParameters: {
        javaEnabled: boolean;
        screenHeight: number;
        screenWidth: number;
        timeZoneOffset: number;
        language: string;
        colorDepth: number;
        deviceType: string;
      };
    };
  };
}

export interface AuthorizeSipResponse {
  success: boolean;
  message: { code: string; text: string };
  data: {
    code: string;
    message?: string;
    expiresAt?: string;
    processor?: {
      order?: { uniqueIdentifier?: string; number?: string };
    };
    [key: string]: unknown;
  };
}

export interface WebhookSiteRequest {
  uuid: string;
  created_at: string;
  content: string;
  method?: string;
}

@Injectable({ providedIn: 'root' })
export class SipPayService {
  /**
   * Siempre `/api-webhook` (mismo origen). `ng serve` lo reescribe a https://webhook.site.
   * Un build estático no reescribe solo: el host tiene que proxyar esa ruta.
   */
  private readonly webhookSiteBase = environment.WEBHOOK_SITE_BASE_URL;

  constructor(private readonly http: HttpClient) {}

  apiBaseUrl(env: SipPayApiEnv): string {
    return SIP_PAY_API_URLS[env] ?? SIP_PAY_API_URLS.QA;
  }

  createOrderNumber(): string {
    return `SIP${Date.now()}`;
  }

  buildSignedElement(orderNumber: string, currencyCode: string, amount: number): string {
    return `${orderNumber}${currencyCode}${parseFloat(String(amount)).toFixed(2)}`;
  }

  buildBrowserParameters(): AuthorizeSipRequest['payment']['clientRequest']['browserParameters'] {
    return {
      javaEnabled: false,
      screenHeight: window.screen?.height ?? 826,
      screenWidth: window.screen?.width ?? 411,
      timeZoneOffset: new Date().getTimezoneOffset(),
      language: navigator.language || 'en-US',
      colorDepth: window.screen?.colorDepth ?? 24,
      deviceType: 'BROWSER',
    };
  }

  /** Misma riqueza de `order` que authorize/token; `payment` es AGORAWEB + cardholder. */
  buildAuthorizeBody(input: SipAuthorizeFormInput): AuthorizeSipRequest {
    const amount = Number(input.amount);
    const sipPhone = String(input.sipPhone).replace(/\s/g, '');
    return {
      order: {
        number: input.orderNumber,
        amount,
        discounts: 0,
        taxes: 0,
        fee: 0,
        shippingAmount: 0,
        country: { code: input.countryCode },
        currency: { code: input.currencyCode },
        products: [
          {
            name: input.productName,
            quantity: 1,
            unitAmount: amount,
            amount,
            sku: input.productSku,
            category: input.productCategory,
            discount: 0,
            shipping: 0,
          },
        ],
        customer: {
          identifier: input.customerIdentifier,
          name: input.name,
          lastName: input.lastName,
          email: input.email,
          phone: input.customerPhone,
          address: {
            country: input.addressCountry,
            levels: [input.addressLevel1, input.addressLevel2, input.addressLevel3],
            line1: input.addressLine1,
            zip: input.addressZip,
          },
          document: {
            type: input.documentType,
            number: String(input.documentNumber).trim(),
          },
        },
        mode: 'STANDAR',
      },
      settings: {
        expiration: null,
        language: input.language,
        paymentMethods: ['AGORAWEB'],
      },
      payment: {
        paymentMethod: 'AGORAWEB',
        cardholder: {
          document: {
            type: input.documentType,
            number: String(input.documentNumber).trim(),
          },
          phone: sipPhone,
        },
        clientRequest: {
          browserParameters: this.buildBrowserParameters(),
        },
      },
    };
  }

  buildAuthHeaders(
    apiKey: string,
    secret: string,
    signedElement: string,
    transactionId: string,
  ): SavedCardAuthHeaders {
    const signature = sha512(`${apiKey}${signedElement}${secret}`);
    return {
      identifier: apiKey,
      signedElement,
      signature,
      transactionId,
    };
  }

  authorizeSip(
    body: AuthorizeSipRequest,
    apiKey: string,
    secret: string,
    apiEnv: SipPayApiEnv,
    transactionId: string,
  ): Observable<AuthorizeSipResponse> {
    const { number, amount, currency } = body.order;
    const signedElement = this.buildSignedElement(number, currency.code, amount);
    const headers = this.buildHttpHeaders(
      this.buildAuthHeaders(apiKey, secret, signedElement, transactionId),
    );
    const url = `${this.apiBaseUrl(apiEnv)}/payment/engine/orders/authorize/sip`;
    return this.http.post<AuthorizeSipResponse>(url, body, { headers });
  }

  /** GET paridad SDK Agora: `order/operation/orders/{uniqueIdentifier}`. */
  queryOrderStatus(
    uniqueIdentifier: string,
    apiKey: string,
    apiEnv: SipPayApiEnv,
  ): Observable<Record<string, unknown>> {
    const base = this.apiBaseUrl(apiEnv);
    const url = `${base}/order/operation/orders/${encodeURIComponent(uniqueIdentifier)}`;
    const headers = new HttpHeaders({ identifier: apiKey });
    return this.http.get<Record<string, unknown>>(url, { headers });
  }

  /**
   * Estados terminales en consulta de orden (mismo criterio que sipPendingProcessor).
   */
  parseTerminalFromOrderBody(body: Record<string, unknown> | null | undefined): {
    isTerminal: boolean;
    code: string;
    success: boolean;
  } {
    if (!body || body['success'] !== true) {
      return { isTerminal: false, code: '', success: false };
    }
    const data = (body['data'] as Record<string, unknown>) || {};
    const paymentResult = (data['paymentResult'] as Record<string, unknown>) || data;
    const result = (paymentResult['result'] as Record<string, unknown>) || {};
    const code = String(result['code'] || data['code'] || '').toUpperCase();
    if (!code) {
      return { isTerminal: false, code: '', success: false };
    }
    const terminal = ['AUTHORIZED', 'DENIED', 'REJECTED', 'ABORTED', 'EXPIRED', 'DECLINED'].includes(
      code,
    );
    const success = code === 'AUTHORIZED' || result['accepted'] === true;
    return { isTerminal: terminal, code, success };
  }

  listWebhookRequests(tokenUuid: string): Observable<WebhookSiteRequest[]> {
    const url = `${this.webhookSiteBase}/token/${encodeURIComponent(tokenUuid)}/requests?sorting=newest`;
    return this.http
      .get<WebhookSiteRequest[] | { data?: WebhookSiteRequest[] | null }>(url)
      .pipe(map((body) => this.normalizeWebhookRequests(body)));
  }

  /** webhook.site responde `{ data, total, ... }`, no un array en la raíz. */
  normalizeWebhookRequests(
    body: WebhookSiteRequest[] | { data?: WebhookSiteRequest[] | null } | null | undefined,
  ): WebhookSiteRequest[] {
    if (Array.isArray(body)) {
      return body;
    }
    const data = body?.data;
    return Array.isArray(data) ? data : [];
  }

  parseWebhookPayload(content: string): { data?: { code?: string }; success?: boolean } | null {
    if (!content?.trim()) {
      return null;
    }
    try {
      return JSON.parse(content) as { data?: { code?: string }; success?: boolean };
    } catch {
      return null;
    }
  }

  webhookMatchesUniqueId(content: string, uniqueIdentifier: string): boolean {
    if (!uniqueIdentifier) {
      return false;
    }
    if (content.includes(uniqueIdentifier)) {
      return true;
    }
    const parsed = this.parseWebhookPayload(content);
    const uid =
      (parsed as { data?: { processor?: { order?: { uniqueIdentifier?: string } } } })?.data
        ?.processor?.order?.uniqueIdentifier;
    return uid === uniqueIdentifier;
  }

  private buildHttpHeaders(auth: SavedCardAuthHeaders): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      identifier: auth.identifier,
      signedElement: auth.signedElement,
      signature: auth.signature,
      'transaction-id': auth.transactionId,
    });
  }
}
